import React, { useEffect, useRef, useState } from "react"
import moment from "moment"
import { H2, TextLink, Text, FlexContainer, TextProps } from "@carrotfertility/carotene"
import { RoundedRectangle } from "../views/atoms/roundedrectangle/RoundedRectangle"
import styled, { ThemeProvider } from "styled-components"
import { BodySmall } from "../views/atoms/Bodies"
import { SinglePathSVG } from "../views/SVG"
import { CarrotPlan, CarrotPlanStep } from "../../types/carrotPlanTypes"
// @ts-ignore
import { carrotOrange } from "css/colors.scss"
import { MaybeLink } from "components/views/atoms/links/MaybeLink"
import InfoOutlineIcon from "../views/atoms/images/InfoOutlineIcon"
import CarrotPlansTourModal from "./CarrotPlansTourModal"
import { useToggle } from "utils/Hooks"
import { Checkbox } from "../views/atoms/Checkbox"
import useUpdatePlanStep from "../carrot-plans/hooks/useUpdatePlanStep"
import { carrotPlanHeroTheme } from "../../themes/formThemes"
import useCarrotPlanMenuItems, { MenuItem } from "../carrot-plans/hooks/useCarrotPlanMenuItems"
import { Error } from "../views/Error"
import { reportErrorMessage } from "../../utils/ErrorReporting"
import { useQueryClient } from "react-query"
import { getCarrotPlanQueryKey } from "components/carrot-plans/hooks/useCarrotPlan"

const PlanHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  margin-top: -4px;
`

const TextAndIconContainer = styled.div`
  margin-right: min(32px);
  margin-top: 4px;
`

const PlanDate = styled.div`
  align-self: center;
  margin-top: 4px;
`

const TourButton = styled(MaybeLink)`
  :hover {
    opacity: 0.5;
    transition: opacity 0.15s ease-in;
  }
`

const TourIcon = styled(InfoOutlineIcon)`
  margin-bottom: -2px;
`

type CarrotPlanHeroProps = {
  plan: CarrotPlan
}

export default function CarrotPlanHero({ plan }: CarrotPlanHeroProps): JSX.Element {
  const [showTourModal, toggleShowTourModal] = useToggle(false)
  const planUpdatedDate = moment(plan.createdAtUtc).format("MMMM D, YYYY")
  const { data: menuItems, isLoading: menuItemsLoading, error: menuItemsError } = useCarrotPlanMenuItems(plan.steps)

  if (menuItemsError) {
    reportErrorMessage(`Unable to retrieve Carrot Plan menu items from Contentful for employee ${plan.employeeId}`)
    return <Error />
  }

  return (
    <>
      {!menuItemsLoading ? (
        <RoundedRectangle backgroundColor="dark-blue" padding="pb2 pt35 pl35 pr35" className="w-100">
          <PlanHeaderRow>
            <TextAndIconContainer>
              <H2 style={{ color: "#ffffff" }}>
                Your Carrot Plan{" "}
                <TourButton onClick={toggleShowTourModal}>
                  <TourIcon color="white" opacity="0.6" />
                </TourButton>
                {showTourModal && <CarrotPlansTourModal onExit={toggleShowTourModal} />}
              </H2>
            </TextAndIconContainer>
            <PlanDate>
              <BodySmall color="white">Updated {planUpdatedDate}</BodySmall>
            </PlanDate>
          </PlanHeaderRow>
          {plan.steps.map((step) => (
            <PlanStep
              key={step.carrotPlanStepId}
              step={step}
              stepDisplayData={menuItems?.find((item) => item.entityId === step.menuItemId)}
            />
          ))}
        </RoundedRectangle>
      ) : null}
    </>
  )
}

const Step = styled.div`
  color: white;
  border-top: rgba(255, 255, 255, 0.1) 1px solid;
  display: flex;
  padding: 24px 0 24px;
  justify-content: space-between;
`

type StepCheckboxAndTextContainerProps = { markedNotRelevant: boolean }
const StepCheckboxAndTextContainer = styled.div<StepCheckboxAndTextContainerProps>`
  display: flex;
  margin-right: min(16px);
  opacity: ${(props) => props.markedNotRelevant && ".5"};
`

const StepLink = styled(TextLink)`
  color: #ffffff;
  border-bottom: none;
  padding-right: 17px;
  margin-left: 16px;
  > svg {
    margin-left: -9px;
    transform: translate(17px);
    vertical-align: top;
    margin-top: 4px;
  }
`

const StepText = styled(Text)<TextProps>`
  cursor: default;
`

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const MenuButton = styled.div`
  align-self: flex-end;
  color: white;
  :hover {
    opacity: 0.5;
    transition: opacity 0.15s ease-in;
  }
`

type PlanStepProps = {
  step: CarrotPlanStep
  stepDisplayData: MenuItem
}

const PlanStep = ({ step, stepDisplayData }: PlanStepProps): JSX.Element => {
  const [stepCompleted, setStepCompleted] = useState(!!step.completedAtUtc)
  const query = useQueryClient()
  const [markedNotRelevant, setMarkedNotRelevant] = useState(!!step.markedNotRelevantAtUtc)
  const [showNotRelevant, toggleShowNotRelevant] = useToggle(false)
  const { mutate } = useUpdatePlanStep()

  const popoverWrapperRef = useRef(null)
  useOutsideAlerter(popoverWrapperRef, () => toggleShowNotRelevant(false))

  const handleStepCompleted = (stepId): void => {
    setStepCompleted(!stepCompleted)
    mutate({ stepId, completed: !stepCompleted }, { onSuccess: () => query.invalidateQueries(getCarrotPlanQueryKey()) })
  }

  const handleNotRelevant = (stepId): void => {
    setMarkedNotRelevant(!markedNotRelevant)
    mutate(
      { stepId, markedNotRelevant: !markedNotRelevant },
      { onSuccess: () => query.invalidateQueries(getCarrotPlanQueryKey()) }
    )
  }

  return (
    <Step>
      <StepCheckboxAndTextContainer markedNotRelevant={markedNotRelevant}>
        <label>
          <ThemeProvider theme={carrotPlanHeroTheme}>
            <Checkbox size="24px" checked={stepCompleted} onChange={() => handleStepCompleted(step.carrotPlanStepId)} />
          </ThemeProvider>
        </label>
        {step.url ? (
          <StepLink href={step.url} target="_blank" onClick={(e) => e.currentTarget.blur()}>
            {step.displayName ? step.displayName : stepDisplayData?.displayText}
            <SinglePathSVG fill={carrotOrange} icon="rightChevron" inline={true} />
          </StepLink>
        ) : (
          <FlexContainer paddingLeftRight="small">
            <Text variant="light">{step.displayName ? step.displayName : stepDisplayData?.displayText}</Text>
          </FlexContainer>
        )}
      </StepCheckboxAndTextContainer>
      <MenuContainer ref={popoverWrapperRef}>
        <MenuButton onClick={toggleShowNotRelevant}>
          <SinglePathSVG icon="threeDots" fill={showNotRelevant ? "#e48400" : "white"} />
        </MenuButton>
        {showNotRelevant && (
          <StepMenu
            markedNotRelevant={markedNotRelevant}
            handleNotRelevant={(stepId) => handleNotRelevant(stepId)}
            stepId={step.carrotPlanStepId}
          />
        )}
      </MenuContainer>
    </Step>
  )
}

const PopoverContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  transform: translate(-85.5%);
  margin-top: 18px;
  z-index: 1;
  filter: drop-shadow(0px 9px 28px rgba(0, 0, 0, 0.25));
  > svg {
    align-self: flex-end;
    margin-right: 14px;
  }
  > div {
    display: flex;
    align-items: center;
    flex-direction: row;
    width: 262px;
    padding: 16px;
    align-self: flex-end;
  }
`

const PopoverText = styled(Text)`
  cursor: default;
  padding-left: 10px;
`

type StepMenuProps = {
  markedNotRelevant: boolean
  handleNotRelevant: (stepId: number) => void
  stepId: number
}

const StepMenu = ({ markedNotRelevant, handleNotRelevant, stepId }: StepMenuProps): JSX.Element => (
  <PopoverContainer>
    <SinglePathSVG icon="popoverArrow" fill="white" />
    <RoundedRectangle>
      <label>
        <Checkbox checked={markedNotRelevant} size="24px" onChange={() => handleNotRelevant(stepId)} />
        <PopoverText style={{ fontWeight: markedNotRelevant ? "bold" : "normal" }}>
          This isn&apos;t relevant to me
        </PopoverText>
      </label>
    </RoundedRectangle>
  </PopoverContainer>
)

function useOutsideAlerter(ref, callback): void {
  useEffect(() => {
    function handleClickOutside(event): void {
      if (ref.current && !ref.current.contains(event.target)) {
        callback()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [ref])
}
